// --------------------------------------------------------------
// Created On: 2025-11-05
// Author: Marco Serrano
//
// Last Modified: 2025-11-19
// Modified By: Marco Serrano
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import { NavigateFunction } from "react-router-dom";
import apiRequest from "./api/apiRequest";
import { API } from "../constants/miscellaneous";
import getApiError from "./api/getApiError";

// Wrapper function for behavior related to saving a quote
export function saveQuoteManager(
  onLoadingChange: (loading: boolean) => void,
  onErrorChange: (error: string) => void,
  onNavigate: NavigateFunction
) {
  // Formats the request body for /sales-quote, makes the request and clears the cart if the function exists before we redirect
  async function saveQuote(quote: SaveQuoteRequestBody, clearCart?: () => void) {
    const requestBody = {
      quoteName: quote.quoteName,
      poNumber: quote.poNumber,
      cart: quote.cartItems,
      shippingCountry: quote.shippingCountry,
      shippingState: quote.shippingState,
      shippingCity: quote.shippingCity,
      shippingDependentLocality: null,
      shippingZipCode: quote.shippingZipCode,
      shippingSortingCode: null,
      shippingAddressLine1: quote.shippingAddressLine1,
      shippingAddressLine2: quote.shippingAddressLine2,
      shippingCompanyName: quote.shippingCompanyName,
      shippingContactName: quote.shippingContactName,
      shippingPhoneNumber: quote.shippingPhoneNumber,
      comment: quote.comment,
      allowPartialShipments: quote.allowPartialShipments,
      shippingBillingType: quote.shippingBillingType,
      shippingBillingAccountNumber: quote.shippingBillingAccountNumber,
      specialInstructions: quote.specialInstructions,
      shipVia: quote.shipVia,
    };
    onLoadingChange(true);
    onErrorChange("");

    const [response] = (await apiRequest(`${API}/sales-quote`, "POST", requestBody)) as [
      Response,
      ResponseBodyPostSalesQuote
    ];

    if (response.ok) {
      if (typeof clearCart === "function") {
        clearCart();
      }
      onNavigate("/sales-quotes");
    } else {
      onErrorChange(await getApiError(response, "Unable to save quote."));
    }

    onLoadingChange(false);
  }

  return { saveQuote };
}

export interface SaveQuoteRequestBody {
  quoteName: string;
  poNumber: string;
  cartItems: CartItem[];
  shippingCountry: string;
  shippingState: string;
  shippingCity: string;
  shippingZipCode: string;
  shippingAddressLine1: string;
  shippingAddressLine2: string;
  shippingCompanyName: string;
  shippingContactName: string;
  shippingPhoneNumber: string;
  comment: string;
  allowPartialShipments: boolean;
  shippingBillingType: string;
  shippingBillingAccountNumber: string;
  specialInstructions: string;
  shipVia: string;
}

interface CartItem {
  partId: number;
  price: number;
  description: string;
  partNumber: string;
  paintColorId: number | null;
  quantity: string;
  impellerTrim: string | null;
  impellerAngle: string | null;
  needBy?: string | null;
  shipBy?: string | null;
}

export interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

interface ResponseBodyPostSalesQuote {
  message: string;
  salesQuoteId: number;
}
